import { Geolocation } from "@capacitor/geolocation";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";
import { isNativeIOS } from "global/utils/platformDetector";

export const LocationErrorType = Object.freeze({
  SERVICES_DISABLED: "SERVICES_DISABLED",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  TIMEOUT: "TIMEOUT",
  UNAVAILABLE: "UNAVAILABLE",
  UNKNOWN: "UNKNOWN",
});

const HIGH_ACCURACY_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};

const LOW_ACCURACY_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 60000,
};

const tienePermisoUbicacion = (status) =>
  status?.location === "granted" || status?.coarseLocation === "granted";

const classifyError = (error) => {
  const msg = (error?.message || String(error)).toLowerCase();
  if (
    msg.includes("location services") ||
    msg.includes("location disabled") ||
    msg.includes("not enabled") ||
    msg.includes("location is unavailable") ||
    msg.includes("location settings") ||
    msg.includes("location is off") ||
    msg.includes("location provider") ||
    msg.includes("gps disabled") ||
    msg.includes("gps is disabled") ||
    msg.includes("turn on location") ||
    msg.includes("settings_change_unavailable")
  ) {
    return LocationErrorType.SERVICES_DISABLED;
  }
  if (msg.includes("permission") || msg.includes("denied")) {
    return LocationErrorType.PERMISSION_DENIED;
  }
  if (msg.includes("timeout") || msg.includes("timed out")) {
    return LocationErrorType.TIMEOUT;
  }
  if (msg.includes("unavailable") || msg.includes("position unavailable")) {
    return LocationErrorType.UNAVAILABLE;
  }
  return LocationErrorType.UNKNOWN;
};

const buildLocationError = (errorType, originalError) => {
  const error = new Error(originalError?.message || errorType);
  error.errorType = errorType;
  error.cause = originalError;
  return error;
};

export const obtenerUbicacionActual = async () => {
  let permisos = await Geolocation.checkPermissions();
  if (!tienePermisoUbicacion(permisos)) {
    permisos = await Geolocation.requestPermissions({
      permissions: ["location", "coarseLocation"],
    });
    if (!tienePermisoUbicacion(permisos)) {
      throw buildLocationError(LocationErrorType.PERMISSION_DENIED);
    }
  }

  let posicion;
  try {
    posicion = await Geolocation.getCurrentPosition(HIGH_ACCURACY_OPTIONS);
  } catch (errorAlta) {
    const tipoAlta = classifyError(errorAlta);
    if (tipoAlta === LocationErrorType.SERVICES_DISABLED) {
      throw buildLocationError(tipoAlta, errorAlta);
    }
    try {
      posicion = await Geolocation.getCurrentPosition(LOW_ACCURACY_OPTIONS);
    } catch (errorBaja) {
      throw buildLocationError(classifyError(errorBaja), errorBaja);
    }
  }

  const { latitude, longitude, accuracy } = posicion.coords;
  return {
    latitud: latitude,
    longitud: longitude,
    precision: accuracy,
  };
};

export const abrirAjustesUbicacion = () =>
  NativeSettings.open({
    optionAndroid: AndroidSettings.Location,
    optionIOS: IOSSettings.App,
  });

export const abrirAjustesApp = () =>
  NativeSettings.open({
    optionAndroid: AndroidSettings.ApplicationDetails,
    optionIOS: IOSSettings.App,
  });

export const buildActionButtonForError = (errorType) => {
  if (errorType === LocationErrorType.SERVICES_DISABLED) {
    if (isNativeIOS()) {
      return { text: "Abrir ajustes", onClick: abrirAjustesApp };
    }
    return { text: "Abrir ajustes de ubicación", onClick: abrirAjustesUbicacion };
  }
  if (errorType === LocationErrorType.PERMISSION_DENIED) {
    return { text: "Abrir ajustes de la app", onClick: abrirAjustesApp };
  }
  // En Android, si el error es genérico/timeout y los permisos estaban dados,
  // la causa más probable es el switch global de ubicación apagado.
  // El botón abre directo Ajustes → Ubicación, donde el usuario ve el toggle.
  // En iOS no aporta porque no se puede deep-linkear a la pantalla útil.
  if (
    !isNativeIOS() &&
    (errorType === LocationErrorType.TIMEOUT ||
      errorType === LocationErrorType.UNAVAILABLE ||
      errorType === LocationErrorType.UNKNOWN)
  ) {
    return { text: "Abrir ajustes de ubicación", onClick: abrirAjustesUbicacion };
  }
  return null;
};

const MENSAJES_ERROR = {
  [LocationErrorType.SERVICES_DISABLED]: {
    android:
      "La ubicación del dispositivo está desactivada. Activala desde el menú de acceso rápido o en Ajustes → Ubicación, y volvé a intentar.",
    ios: "La ubicación del dispositivo está desactivada. Andá a Ajustes → Privacidad y seguridad → Localización, activala, y volvé a intentar.",
  },
  [LocationErrorType.PERMISSION_DENIED]: {
    android:
      "La aplicación no tiene permiso para usar la ubicación. Habilitalo desde Ajustes → Apps → DIM Portal → Permisos.",
    ios: "La aplicación no tiene permiso para usar la ubicación. Habilitalo desde Ajustes → DIM Portal → Localización.",
  },
  [LocationErrorType.TIMEOUT]: {
    android:
      "No pudimos obtener tu ubicación. Verificá que la ubicación del dispositivo esté activada y volvé a intentar.",
    ios: "No pudimos obtener tu ubicación a tiempo. Verificá la señal de GPS y volvé a intentar.",
  },
  [LocationErrorType.UNAVAILABLE]: {
    android:
      "La ubicación no está disponible. Verificá que la ubicación del dispositivo esté activada y volvé a intentar.",
    ios: "La ubicación no está disponible en este momento. Probá nuevamente en unos segundos.",
  },
  [LocationErrorType.UNKNOWN]: {
    android:
      "No pudimos obtener tu ubicación. Verificá que la ubicación del dispositivo esté activada y volvé a intentar.",
    ios: "No se ha podido detectar su ubicación.",
  },
};

export const buildLocationErrorPresentation = (errorType) => {
  const tipo = errorType || LocationErrorType.UNKNOWN;
  const plataforma = isNativeIOS() ? "ios" : "android";
  const mensajes = MENSAJES_ERROR[tipo] || MENSAJES_ERROR[LocationErrorType.UNKNOWN];
  return {
    message: mensajes[plataforma],
    actionButton: buildActionButtonForError(tipo),
  };
};
